import { useCallback, useState, useRef } from 'react';
import { usePlotsStore } from '@/store/plots';
import { computeFigureHash } from '@/lib/drafts/localDraft';

interface PlotState {
  plotlyJson: any;
  timestamp: number;
  operation: string;
  operationType: 'fast' | 'ai';
}

// Track history per plotId + baselineHash combination
const historyMap = new Map<string, { history: PlotState[]; historyIndex: number }>();
const MAX_HISTORY_MAP_SIZE = 100; // Prevent unbounded growth

function getHistoryKey(plotId: string | null | undefined, baselineHash: string | null | undefined): string {
  return `${plotId ?? 'unknown'}:${baselineHash ?? 'unknown'}`;
}

export function usePlotEditHistory() {
  const { currentPlot, setCurrentPlot } = usePlotsStore();
  const [history, setHistory] = useState<PlotState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const lastPlotKeyRef = useRef<string>('');
  const maxHistorySize = 50; // Keep more history for complex plots

  // Initialize history when plot changes - only when plotId or baselineHash changes
  const initializeHistory = useCallback((plot: any) => {
    if (!plot?.plotlyJson) {
      console.log('❌ Cannot initialize history - no plotlyJson found in plot:', plot);
      setHistory([]);
      setHistoryIndex(-1);
      return;
    }
    
    const plotId = plot.id;
    
    // Skip initialization in unbound mode (when plotId is undefined/null)
    // This prevents history from being keyed by old plot IDs when entering "Create New Plot"
    if (!plotId) {
      console.log('📚 Skipping history initialization - unbound mode (no plotId)');
      setHistory([]);
      setHistoryIndex(-1);
      lastPlotKeyRef.current = '';
      return;
    }
    
    const baselineHash = plot.baselineHash || computeFigureHash(plot.plotlyJson);
    const historyKey = getHistoryKey(plotId, baselineHash);
    
    // If this is the same plot + baseline, don't reinitialize
    if (historyKey === lastPlotKeyRef.current) {
      console.log('📚 History key unchanged, skipping reinitialization:', historyKey);
      return;
    }
    
    lastPlotKeyRef.current = historyKey;
    
    // Check if we have existing history for this key
    const existing = historyMap.get(historyKey);
    if (existing) {
      console.log('📚 Restoring existing history for key:', historyKey, 'with', existing.history.length, 'entries');
      setHistory(existing.history);
      setHistoryIndex(existing.historyIndex);
      return;
    }
    
    // Initialize new history
    const initialState: PlotState = {
      plotlyJson: JSON.parse(JSON.stringify(plot.plotlyJson)),
      timestamp: Date.now(),
      operation: 'Plot loaded',
      operationType: 'fast'
    };
    console.log('📚 History initialized with initial state for key:', historyKey);
    
    const newHistory = [initialState];
    setHistory(newHistory);
    setHistoryIndex(0);
    
    // Store in map for future use
    historyMap.set(historyKey, { history: newHistory, historyIndex: 0 });
  }, []);

  // Save current state to history
  const saveToHistory = useCallback((operation: string, operationType: 'fast' | 'ai' = 'fast', plotlyJsonOverride?: any) => {
    const plotlyJson = plotlyJsonOverride || currentPlot?.plotlyJson;
    if (!plotlyJson || !currentPlot) return;

    const plotId = currentPlot.id;
    const baselineHash = currentPlot.baselineHash || computeFigureHash(plotlyJson);
    const historyKey = getHistoryKey(plotId, baselineHash);

    const newState: PlotState = {
      plotlyJson: JSON.parse(JSON.stringify(plotlyJson)),
      timestamp: Date.now(),
      operation,
      operationType
    };

    setHistory(prevHistory => {
      // Remove any future states if we're in the middle of history
      const currentHistory = prevHistory.slice(0, historyIndex + 1);
      
      // Add new state
      const newHistory = [...currentHistory, newState];
      
      // Limit history size
      let finalHistory: PlotState[];
      let finalIndex: number;
      if (newHistory.length > maxHistorySize) {
        finalHistory = newHistory.slice(-maxHistorySize);
        finalIndex = finalHistory.length - 1;
      } else {
        finalHistory = newHistory;
        finalIndex = newHistory.length - 1;
      }
      
      // Update map with cleanup to prevent unbounded growth
      historyMap.set(historyKey, { history: finalHistory, historyIndex: finalIndex });
      
      // Cleanup old entries if map grows too large
      if (historyMap.size > MAX_HISTORY_MAP_SIZE) {
        const oldestKey = historyMap.keys().next().value;
        historyMap.delete(oldestKey);
      }
      
      setHistoryIndex(finalIndex);
      
      return finalHistory;
    });

    console.log(`📚 Saved to history: ${operation} (index will be ${historyIndex + 1})`);
  }, [currentPlot, historyIndex]);

  // Apply fast update with history tracking
  const applyFastUpdate = useCallback((
    updateType: string, 
    value: any, 
    updateFunction: (plotlyJson: any, value: any) => void
  ) => {
    if (!currentPlot?.plotlyJson) return;

    console.log(`🔄 ApplyFastUpdate: ${updateType} = ${value}`);
    console.log('📊 Current plotlyJson before update:', currentPlot.plotlyJson);

    const startTime = performance.now();
    
    // Clone the plotly JSON to avoid mutation
    const updatedPlotlyJson = JSON.parse(JSON.stringify(currentPlot.plotlyJson));
    
    // Apply the update
    updateFunction(updatedPlotlyJson, value);
    
    console.log('📊 Updated plotlyJson after update:', updatedPlotlyJson);
    
    // Update the plot immediately
    const updatedPlot = {
      ...currentPlot,
      plotlyJson: updatedPlotlyJson
    };
    
    setCurrentPlot(updatedPlot);
    
    // IMPORTANT: Save the NEW state to history AFTER making changes
    // Use setTimeout to ensure the state update completes first
    setTimeout(() => {
      saveToHistory(`${updateType}: ${value}`, 'fast', updatedPlotlyJson);
    }, 0);
    
    const duration = performance.now() - startTime;
    
    // Dispatch performance event
    window.dispatchEvent(new CustomEvent('plot-fast-update', {
      detail: { updateType, duration, value }
    }));

    return duration;
  }, [currentPlot, setCurrentPlot, saveToHistory]);

  // Undo functionality
  const undo = useCallback(() => {
    console.log(`🔄 Undo called - historyIndex: ${historyIndex}, history.length: ${history.length}`);
    console.log('📚 Current history:', history.map((h, i) => `${i}: ${h.operation}`));
    
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previousState = history[newIndex];
      
      console.log(`🔄 Attempting to restore state ${newIndex}: ${previousState.operation}`);
      console.log('📊 Previous plotlyJson:', previousState.plotlyJson);
      console.log('📊 Current plotlyJson:', currentPlot?.plotlyJson);
      
      if (currentPlot && previousState) {
        const restoredPlot = {
          ...currentPlot,
          plotlyJson: JSON.parse(JSON.stringify(previousState.plotlyJson))
        };
        
        console.log('📊 Restored plotlyJson:', restoredPlot.plotlyJson);
        console.log('🔄 Setting current plot and history index...');
        
        setCurrentPlot(restoredPlot);
        setHistoryIndex(newIndex);
        
        console.log(`✅ Undo successful: ${previousState.operation} (index: ${newIndex})`);
        return true;
      }
    }
    console.log(`❌ Undo failed: historyIndex=${historyIndex}, history.length=${history.length}`);
    return false;
  }, [currentPlot, setCurrentPlot, historyIndex, history]);

  // Redo functionality
  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const nextState = history[newIndex];
      
      if (currentPlot && nextState) {
        const restoredPlot = {
          ...currentPlot,
          plotlyJson: JSON.parse(JSON.stringify(nextState.plotlyJson))
        };
        
        setCurrentPlot(restoredPlot);
        setHistoryIndex(newIndex);
        
        console.log(`↪️ Redo: ${nextState.operation} (index: ${newIndex})`);
        return true;
      }
    }
    console.log(`↪️ Redo failed: historyIndex=${historyIndex}, history.length=${history.length}`);
    return false;
  }, [currentPlot, setCurrentPlot, historyIndex, history]);

  // Check if undo/redo is available
  const canUndo = useCallback(() => {
    const result = history.length > 1 && historyIndex > 0;
    console.log(`🔍 canUndo: ${result} (historyIndex: ${historyIndex}, history.length: ${history.length})`);
    return result;
  }, [historyIndex, history.length]);

  const canRedo = useCallback(() => {
    const result = historyIndex < history.length - 1 && history.length > 0;
    console.log(`🔍 canRedo: ${result} (historyIndex: ${historyIndex}, history.length: ${history.length})`);
    return result;
  }, [historyIndex, history.length]);

  // Get history info for debugging
  const getHistoryInfo = useCallback(() => {
    return {
      totalStates: history.length,
      currentIndex: historyIndex,
      canUndo: canUndo(),
      canRedo: canRedo(),
      currentOperation: history[historyIndex]?.operation || 'None',
      recentOperations: history.slice(-5).map(state => state.operation)
    };
  }, [history, historyIndex, canUndo, canRedo]);

  // Reset history to a specific plotlyJson state (used by Reset to baseline)
  const resetTo = useCallback((plotlyJson: any, operationLabel = "Reset to saved") => {
    if (!plotlyJson || !currentPlot) return;
    
    const plotId = currentPlot.id;
    const baselineHash = currentPlot.baselineHash || computeFigureHash(plotlyJson);
    const historyKey = getHistoryKey(plotId, baselineHash);
    
    // Hard-reset history to exactly one entry (the baseline)
    const resetState: PlotState = {
      plotlyJson: JSON.parse(JSON.stringify(plotlyJson)),
      timestamp: Date.now(),
      operation: operationLabel,
      operationType: 'fast'
    };
    
    const newHistory = [resetState];
    setHistory(newHistory);
    setHistoryIndex(0);
    
    // Update map with reset history
    historyMap.set(historyKey, { history: newHistory, historyIndex: 0 });
    
    console.log(`📚 History reset to baseline: ${operationLabel}`);
  }, [currentPlot]);

  return {
    initializeHistory,
    saveToHistory,
    applyFastUpdate,
    undo,
    redo,
    canUndo,
    canRedo,
    getHistoryInfo,
    resetTo
  };
}
